import { action } from 'mobx';
import { DetailedScheduleStore } from './DetailedScheduleStore';
import { GlobalModalStore } from './GlobalModalStore';
import HsRoomPriorityStore from './HsRoomPriorityStore';
import { JobPasteStore } from './JobPasteStore';
import { NavbarStore } from './NavbarStore';
import { ReleasingPriorityStore } from './ReleasingPriorityStore/ReleasingPriorityStore';
import { ScheduleAttainmentStore } from './ScheduleAttainmentStore';
import { StagingPriorityStore } from './StagingPriorityStore/StagingPriorityStore';
import { UserStore } from './UserStores/userStore';
import RoomEditingStore from './RoomEditingStore';

import {
  bottleByRoomCalculationStore,
  BottleByRoomCalculationStore,
  cleanByRoomStore,
  CleanByRoomStore,
  cleanCalculationsStore,
  CleanCalculationsStore,
  optimizedCalculationsStore,
  OptimizedCalculationsStore,
  productCalculationsStore,
  ProductCalculationsStore,
  scheduleCalculationsStore,
  ScheduleCalculationsStore
} from './CalculationStore';
import { JobTagsStore } from './JobtagStore/JobTagStore';
import {
  bpmByNdcAndJobTypeStore,
  BpmByNdcAndJobTypeStore,
  cleanTimeStore,
  CleanTimeStore,
  cleanTypeStore,
  CleanTypeStore,
  clearTimeStore,
  ClearTimeStore,
  coatingTypeStore,
  CoatingTypeStore,
  defaultBpmByPillCountStore,
  DefaultBpmByPillCountStore,
  drugReferenceFgNdcStore,
  DrugReferenceFgNdcStore,
  drugReferenceFgNdcToBulkNdcStore,
  DrugReferenceFgNdcToBulkNdcStore,
  drugReferenceByConceptCodeStore,
  DrugReferenceByConceptCodeStore,
  lvTimeStore,
  LvTimeStore,
  roomBpmByRunTypeStore,
  RoomBpmByRunTypeStore,
  roomStore,
  RoomStore,
  runTypeStore,
  RunTypeStore,
  setupTimeStore,
  SetupTimeStore,
  shiftTimingStore,
  ShiftTimingStore,
  standardAndRecipeBpmStore,
  StandardAndRecipeBpmStore,
  statusCodesStore,
  StatusCodesStore
} from './ReferenceStore';
import { ExtraDustyProductsStore } from './StandardTimeLegend';
import { VisibilityBoardStore } from './VisibilityBoardStore';
import { SupportTicketStore } from './SupportTicketStore/SupportTicketStore';

export class RootStore {
  // Core application stores
  UserStore: UserStore;
  NavbarStore: NavbarStore;
  JobPasteStore: JobPasteStore;
  GlobalModalStore: GlobalModalStore;
  ScheduleAttainmentStore: ScheduleAttainmentStore;
  VisibilityBoardStore: VisibilityBoardStore;
  StagingPriorityStore: StagingPriorityStore;
  ReleasingPriorityStore: ReleasingPriorityStore;
  RoomEditingStore: RoomEditingStore;
  SupportTicketStore: SupportTicketStore;

  // Reference stores (directly imported instances)
  roomStore = roomStore;
  shiftTimingStore = shiftTimingStore;
  runTypeStore = runTypeStore;
  coatingTypeStore = coatingTypeStore;
  statusCodesStore = statusCodesStore;
  cleanTimeStore = cleanTimeStore;
  cleanTypeStore = cleanTypeStore;
  drugReferenceFgNdcStore = drugReferenceFgNdcStore;
  drugReferenceFgNdcToBulkNdcStore = drugReferenceFgNdcToBulkNdcStore;
  drugReferenceByConceptCodeStore = drugReferenceByConceptCodeStore;
  defaultBpmByPillCountStore = defaultBpmByPillCountStore;
  standardAndRecipeBpmStore = standardAndRecipeBpmStore;
  roomBpmByRunTypeStore = roomBpmByRunTypeStore;
  bpmByNdcAndJobTypeStore = bpmByNdcAndJobTypeStore;
  clearTimeStore = clearTimeStore;
  setupTimeStore = setupTimeStore;
  lvTimeStore = lvTimeStore;

  // Calculation stores
  cleanCalculationsStore = cleanCalculationsStore;
  productCalculationsStore = productCalculationsStore;
  optimizedCalculationsStore = optimizedCalculationsStore;
  bottleByRoomCalculationStore = bottleByRoomCalculationStore;
  cleanByRoomStore = cleanByRoomStore;

  // Job tags store
  JobTagsStore: JobTagsStore;
  scheduleCalculationsStore = scheduleCalculationsStore;

  // Detailed schedule store
  DetailedScheduleStore: DetailedScheduleStore;
  // HS Room Priority store
  hsRoomPriorityStore: HsRoomPriorityStore;
  // standard time legend store
  extraDustyProductsStore: ExtraDustyProductsStore;

  constructor() {
    // Initialize core stores
    this.UserStore = new UserStore();
    this.NavbarStore = new NavbarStore();
    this.JobPasteStore = new JobPasteStore();
    this.GlobalModalStore = new GlobalModalStore();
    this.ScheduleAttainmentStore = new ScheduleAttainmentStore();
    this.JobTagsStore = new JobTagsStore();
    this.VisibilityBoardStore = new VisibilityBoardStore();
    this.DetailedScheduleStore = new DetailedScheduleStore();
    this.StagingPriorityStore = new StagingPriorityStore();
    this.ReleasingPriorityStore = new ReleasingPriorityStore();
    this.hsRoomPriorityStore = new HsRoomPriorityStore();
    this.JobTagsStore = new JobTagsStore();
    this.extraDustyProductsStore = new ExtraDustyProductsStore();
    this.RoomEditingStore = new RoomEditingStore();
    this.SupportTicketStore = new SupportTicketStore();
  }

  @action resetRoot = () => {
    // Reset core stores
    this.UserStore = new UserStore();
    this.NavbarStore = new NavbarStore();
    this.JobPasteStore = new JobPasteStore();
    this.GlobalModalStore = new GlobalModalStore();

    // Reset reference stores
    this.roomStore = new RoomStore();
    this.shiftTimingStore = new ShiftTimingStore();
    this.runTypeStore = new RunTypeStore();
    this.coatingTypeStore = new CoatingTypeStore();
    this.statusCodesStore = new StatusCodesStore();
    this.cleanTimeStore = new CleanTimeStore();
    this.drugReferenceFgNdcStore = new DrugReferenceFgNdcStore();
    this.drugReferenceFgNdcToBulkNdcStore = new DrugReferenceFgNdcToBulkNdcStore();
    this.drugReferenceByConceptCodeStore = new DrugReferenceByConceptCodeStore();
    this.cleanTypeStore = new CleanTypeStore();
    this.defaultBpmByPillCountStore = new DefaultBpmByPillCountStore();
    this.standardAndRecipeBpmStore = new StandardAndRecipeBpmStore();
    this.roomBpmByRunTypeStore = new RoomBpmByRunTypeStore();
    this.bpmByNdcAndJobTypeStore = new BpmByNdcAndJobTypeStore();
    this.clearTimeStore = new ClearTimeStore();
    this.setupTimeStore = new SetupTimeStore();
    this.lvTimeStore = new LvTimeStore();

    // Reset calculation stores
    this.cleanCalculationsStore = new CleanCalculationsStore();
    this.productCalculationsStore = new ProductCalculationsStore();
    this.optimizedCalculationsStore = new OptimizedCalculationsStore();
    this.bottleByRoomCalculationStore = new BottleByRoomCalculationStore();
    this.cleanByRoomStore = new CleanByRoomStore();

    // Reset job tag store
    this.JobTagsStore = new JobTagsStore();
    this.scheduleCalculationsStore = new ScheduleCalculationsStore();
    this.VisibilityBoardStore = new VisibilityBoardStore();
    this.StagingPriorityStore = new StagingPriorityStore();
    this.ReleasingPriorityStore = new ReleasingPriorityStore();

    // Reset detailed schedule store
    this.DetailedScheduleStore = new DetailedScheduleStore();

    // Reset HS room priority store
    this.hsRoomPriorityStore = new HsRoomPriorityStore();

    // Reset standard time legend store
    this.extraDustyProductsStore = new ExtraDustyProductsStore();

    // Reset room editing store
    this.RoomEditingStore = new RoomEditingStore();

    // Reset support ticket store
    this.SupportTicketStore = new SupportTicketStore();
  };
}

// Global store instance
export const rootStore = new RootStore();
